import { useEffect, useMemo, useRef, useState } from "react";
import { format, startOfMonth, endOfMonth, parseISO } from "date-fns";
import Button from "../../../../common/components/button";
import DayPickerInput from "../../../components/common/DayPickerInput";
import useToastStore from "../../../store/useToastStore";
import { useGetDailyMemo } from "../../../../common/hooks/console/transaction/useGetDailyMemo";
import { useGetDailyMemoDates } from "../../../../common/hooks/console/transaction/useGetDailyMemoDates";
import { usePutDailyMemo } from "../../../../common/hooks/console/transaction/usePutDailyMemo";

const MAX_LENGTH = 500;

/**
 * 일자별 공유 메모
 *
 * 주문판매 헤더에 띄우는 직원 공용 메모장. 날짜당 한 칸이고 콘솔 사용자 누구나 고칠 수 있다.
 * 기본 날짜는 조회 기간의 시작일을 따라가되, 달력으로 아무 날짜나 직접 골라볼 수 있다.
 * (전월 조회처럼 기간이 넓으면 시작일 하나만 보이므로 달력이 없으면 다른 날짜를 못 본다)
 * 지점 포털에는 노출하지 않는다. (서버도 isAuthLevel7 + branch-scope 로 LV6 을 막는다)
 */
const DailyMemo = ({ memoDate }: { memoDate: string }) => {
    const { addToast } = useToastStore();

    /*
    // 달력으로 고른 날짜가 있으면 그 날짜를, 없으면 기간에서 넘어온 날짜를 쓴다.
    // 기간 버튼을 다시 누르면 memoDate 가 바뀌면서 달력 선택이 풀린다.
    */
    const [pickedDate, setPickedDate] = useState<string | null>(null);
    const activeDate = pickedDate ?? memoDate;

    /* 달력에 보이는 달. 점을 찍을 범위를 이 달로 잡는다 */
    const [visibleMonth, setVisibleMonth] = useState<Date>(() => (memoDate ? parseISO(memoDate) : new Date()));

    const { data } = useGetDailyMemo(activeDate);
    const { data: markedData } = useGetDailyMemoDates(
        format(startOfMonth(visibleMonth), "yyyy-MM-dd"),
        format(endOfMonth(visibleMonth), "yyyy-MM-dd"),
    );

    const [text, setText] = useState("");

    /*
    // 입력 중에 서버 값이 덮어쓰지 않도록, 편집을 시작하면 동기화를 멈춘다.
    // 날짜가 바뀌거나 저장이 끝나면 다시 서버 값을 따라간다.
    */
    const dirtyRef = useRef(false);

    const { mutate, isPending } = usePutDailyMemo(() => {
        dirtyRef.current = false;
        addToast({ type: "success", message: "공유메모가 저장되었습니다." });
    });

    /* 기간 버튼으로 날짜가 바뀌면 달력 선택을 풀고 그 달로 이동한다 */
    useEffect(() => {
        setPickedDate(null);
        if (memoDate) setVisibleMonth(parseISO(memoDate));
    }, [memoDate]);

    useEffect(() => {
        dirtyRef.current = false;
        setText("");
    }, [activeDate]);

    useEffect(() => {
        if (data && !dirtyRef.current) {
            setText(data.contents ?? "");
        }
    }, [data]);

    const markedDates = useMemo(() => (markedData?.dates ?? []).map(date => parseISO(date)), [markedData]);

    /*
    // DayPickerInput 은 initialDate 참조가 바뀔 때마다 내부 상태를 다시 세팅한다.
    // 매 렌더마다 새 Date 를 넘기면 그 effect 가 끝없이 돌므로 날짜 문자열 기준으로 고정한다.
    */
    const selectedDateObj = useMemo(() => (activeDate ? parseISO(activeDate) : undefined), [activeDate]);

    const handleSave = () => {
        if (!activeDate || isPending) return;
        mutate({ memo_date: activeDate, contents: text });
    };

    /*
    // 헤더 윗줄에서 제목 묶음과 페이지 이동 사이의 남는 폭을 차지한다.
    // 헤더는 줄바꿈 없이 가로 스크롤되므로, 최소 너비로 입력칸이 찌그러지지 않게만 한다.
    */
    return (
        <div className="flex flex-1 min-w-[300px] mx-4 max-w-[440px] flex-col justify-center gap-1">
            <div className="flex items-center gap-2">
                <p className="text-sm font-bold whitespace-nowrap">공유메모</p>
                <div className="w-[150px] shrink-0 border border-gray4 rounded-lg px-2 h-[30px] flex items-center">
                    <DayPickerInput
                        initialDate={selectedDateObj}
                        markedDates={markedDates}
                        onMonthChange={setVisibleMonth}
                        onDateChange={date => {
                            if (!date) return;
                            setPickedDate(format(date, "yyyy-MM-dd"));
                        }}
                    />
                </div>
                {data?.update_date && (
                    <p className="text-xs text-gray3 truncate">
                        최종수정 {data.m_name} {data.update_date}
                    </p>
                )}
                <span className="text-xs text-gray3 ml-auto whitespace-nowrap">
                    {text.length}/{MAX_LENGTH}
                </span>
            </div>
            <div className="flex items-start gap-2">
                <textarea
                    rows={2}
                    maxLength={MAX_LENGTH}
                    placeholder="직원 공유 내용을 입력하세요"
                    className="flex-1 min-w-0 resize-none rounded-lg border border-gray4 px-3 py-[7px] text-sm outline-0 placeholder:text-grayicon focus:border-primary1"
                    value={text}
                    onChange={e => {
                        dirtyRef.current = true;
                        setText(e.target.value);
                    }}
                />
                <Button
                    size="S"
                    status="regist"
                    className="px-3 shrink-0"
                    disabled={isPending}
                    onClick={handleSave}
                >
                    저장
                </Button>
            </div>
        </div>
    );
};

export default DailyMemo;
